import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { ShareIcon, DownloadIcon, PrinterIcon } from "@heroicons/react/outline";
import Modal from "../../../components/Modal";
import { FaExclamationTriangle } from "react-icons/fa";
import { POCService, pocDataMap } from "../../../services";
import { Skeleton } from "../../../components/ui/skeleton";
import { QRCodeCanvas } from "qrcode.react";
import { useRef } from "react";
import { GoLocation } from "react-icons/go";
import { toast } from "react-toastify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { createItemStore } from "../../../stores/createItemStore";

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "pocs";
const useItems = createItemStore(
  storeName,
  (poc) => poc.POCId,
  (args) => POCService.getPOCDetails(args[0]),
  pocDataMap
);
// ========================================================

const EditPOCPage = () => {
  const navigate = useNavigate();
  const { POCID } = useParams();
  const qrCanvasRef = useRef(null);

  const gurl = "https://maps.app.goo.gl/eFEMEZSUtFRg4VgS8";

  const [activeTab, setActiveTab] = useState("general");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [generalInfo, setGeneralInfo] = useState({
    pocId: "",
    pocName: "",
    createDate: "",
    addressLine1: "",
    addressLine2: "",
    postcode: "",
    city: "",
    state: "",
    country: "",
    map: "",
  });

  const handleSave = async () => {
    const payload = {
      PocId: POCID, // From URL params
      Name: generalInfo.pocName,
      AddressLine1: generalInfo.addressLine1,
      AddressLine2: generalInfo.addressLine2,
      Postcode: generalInfo.postcode,
      City: generalInfo.city,
      State: generalInfo.state,
      Country: generalInfo.country,
      Map: generalInfo.map,
    };

    try {
      console.log("Saving POC Data:", payload);
      await POCService.editPOCDetails(payload);
      toast.success("🎉 POC details updated successfully!", {
        autoClose: 2000,
      });
      navigate("/poc");
    } catch (error) {
      toast.error("❌ Failed to update POC details. Please try again later", {
        autoClose: 2000,
      });
      console.error("Error updating POC:", error);
    }
  };

  const handleConfirmDelete = () => {
    toast.success("🎉 POC has been Deleted", {
      autoClose: 2000,
    });
    setShowDeleteModal(false);
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleGeneralInfoChange = (e) => {
    const { name, value } = e.target;
    setGeneralInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleShare = () => {
    console.log("Share clicked");
    const canvas = qrCanvasRef.current;
    if (canvas) {
      canvas.toBlob(async (blob) => {
        const file = new File([blob], "qr-code.png", { type: "image/png" });
        if (navigator.share) {
          try {
            await navigator.share({
              title: "QR Code",
              text: "Scan this QR code",
              files: [file],
            });
          } catch (err) {
            console.error("Error sharing", err);
          }
        } else {
          alert("Sharing is not supported in your browser.");
        }
      });
    }
  };

  const handleDownload = () => {
    const canvas = qrCanvasRef.current;
    if (canvas) {
      const url = canvas.toDataURL("image/png"); // this is just the QR code image
      const link = document.createElement("a");
      link.href = url;
      link.download = "qr-code.png";
      link.click();
    }
  };

  const handlePrint = () => {
    console.log("Print clicked");
    const canvas = qrCanvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      const windowContent = `
      <html>
        <head><title>Print QR Code</title></head>
        <body>
          <img src="${dataUrl}" style="width:180px;" />
          <script>
            window.onload = function () {
              window.print();
              window.onafterprint = function () { window.close(); };
            }
          </script>
        </body>
      </html>
    `;
      const printWindow = window.open("", "_blank");
      printWindow.document.write(windowContent);
      printWindow.document.close();
    }
  };

  // Add this function to generate the iframe
  const generateIframe = (userProvidedLink) => {
    if (!userProvidedLink) return null;

    // Handle standard place URLs
    if (userProvidedLink.includes("/place/")) {
      const placePath = userProvidedLink.split("/place/")[1].split("/")[0];
      const embedUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d00000.00000000000!2d0.000000000000!3d0.000000000000!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s${encodeURIComponent(
        placePath
      )}!2s0x0:0x0!5e0!3m2!1sen!2sus!4v00000000000!5m2!1sen!2sus`;
      return (
        <iframe
          src={embedUrl}
          width="100%"
          height="300"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
        />
      );
    }
    // Handle coordinates-based URLs (?q=lat,lng)
    else if (userProvidedLink.includes("?q=")) {
      const coords = userProvidedLink.split("?q=")[1].split("&")[0];
      const embedUrl = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d00000.00000000000!2d${
        coords.split(",")[1]
      }!3d${
        coords.split(",")[0]
      }!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v00000000000!5m2!1sen!2sus`;
      return (
        <iframe
          src={embedUrl}
          width="100%"
          height="300"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
        />
      );
    }
    // Fallback: Just use the URL directly (works for most links)
    else {
      return (
        <iframe
          src={userProvidedLink.replace(
            "https://www.google.com/maps/",
            "https://www.google.com/maps/embed/v1/view?key=YOUR_API_KEY&center="
          )}
          width="100%"
          height="300"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
        />
      );
    }
  };

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { fetchItems, getSortedFiltered } = useItems((state) => state);
  const data = getSortedFiltered((poc) => poc.POCId === POCID);
  const poc = data?.[0] ?? null;
  useEffect(() => {
    fetchItems({}, [POCID]);
  }, [POCID]);
  // ========================================================

  // Always update form state when poc changes, but only if the user hasn't started editing
  useEffect(() => {
    if (poc) {
      setGeneralInfo((prev) => {
        // Only update if the form is still at its initial state (all fields empty)
        const isPristine = Object.values(prev).every((v) => !v);
        if (isPristine) {
          return {
            pocId: poc.POCId || "",
            pocName: poc.name || "",
            createDate: poc.createDate || "",
            addressLine1: poc.addressLine1 || "",
            addressLine2: poc.addressLine2 || "",
            postcode: poc.postcode || "",
            city: poc.city || "",
            state: poc.state || "",
            country: poc.country || "",
            map: poc.map || "",
          };
        }
        return prev;
      });
    }
  }, [poc]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 pb-1 rounded-lg">
            <div className="mb-6">
              <div className="sm:flex sm:justify-between sm:items-center">
                {/* Left: Icon + Text */}
                <div className="flex items-start sm:items-center gap-4">
                  <div className="bg-lime-400 p-3 sm:p-4 rounded-full">
                    <GoLocation className="w-10 h-10 sm:w-16 sm:h-16 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-[#111827]">
                      {poc ? (
                        poc.name
                      ) : (
                        <Skeleton className="w-32 h-4" />
                      )}
                    </h1>
                    <div className="mt-4 sm:mt-6 flex space-x-8">
                      <div>
                        <div className="text-sm font-semibold text-[#6B7280]">
                          Date Created
                        </div>
                        <div className="text-sm text-[#111827]">
                          {poc ? (
                            poc.createDate
                          ) : (
                            <Skeleton className="w-32 h-4" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Buttons (inline only on sm and above) */}
                <div className="hidden sm:flex gap-4">
                  <Button
                    variant={"hollowGreen"}
                    onClick={() => navigate(`/poc/view/${poc.POCId}`)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>Save</Button>
                </div>
              </div>

              {/* Buttons on small screens only */}
              <div className="flex sm:hidden flex-col gap-2 mt-4">
                <Button
                  variant={"hollowGreen"}
                  className="w-full"
                  onClick={() => navigate(`/poc/view/${poc.POCId}`)}
                >
                  Cancel
                </Button>
                <Button className="w-full" onClick={handleSave}>
                  Save
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>
          </div>

          {/* Form section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6 mt-0 bg-white shadow rounded-r-lg rounded-b-lg ">
            {/* First Name Field */}
            <div>
              <Label>POC Name</Label>
              {poc ? (
                <Input
                  type="text"
                  name="pocName"
                  value={generalInfo.pocName}
                  onChange={handleGeneralInfoChange}
                  placeholder="Enter POC Name"
                />
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}
            </div>

            {/* QR Code Block */}
            <div className="flex flex-col sm:flex-row sm:items-start gap-4 border border-gray-300 rounded p-4">
              {poc?.qrLink ? (
                <QRCodeCanvas
                  value={poc.qrLink}
                  size={180}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  ref={qrCanvasRef}
                />
              ) : (
                <Skeleton className="w-48 h-48" />
              )}

              <div className="flex flex-col gap-2 sm:pl-6 w-full sm:w-auto">
                <Button variant={"hollowGreen"} onClick={handleShare}>
                  <ShareIcon className="h-5 w-5 mr-2" />
                  Share
                </Button>
                <Button variant={"hollowGreen"} onClick={handleDownload}>
                  <DownloadIcon className="h-5 w-5 mr-2" />
                  Download
                </Button>
                <Button variant={"hollowGreen"} onClick={handlePrint}>
                  <PrinterIcon className="h-5 w-5 mr-2" />
                  Print
                </Button>
              </div>
            </div>

            {/* Address Field */}
            <div>
              <Label>Address 1</Label>
              {poc ? (
                <Input
                  type="text"
                  name="addressLine1"
                  value={generalInfo.addressLine1}
                  onChange={handleGeneralInfoChange}
                  placeholder="Enter Address"
                />
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}
            </div>

            <div>
              <Label>Address 2</Label>
              {poc ? (
                <Input
                  type="text"
                  name="addressLine2"
                  value={generalInfo.addressLine2}
                  onChange={handleGeneralInfoChange}
                  placeholder="Enter Address"
                />
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}
            </div>

            {/* Postcode Field */}
            <div>
              <Label>Postcode</Label>
              {poc ? (
                <Input
                  type="text"
                  name="postcode"
                  value={generalInfo.postcode}
                  onChange={handleGeneralInfoChange}
                  placeholder="Enter Postcode"
                />
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}
            </div>

            {/* City Field */}
            <div>
              <Label>City</Label>
              {poc ? (
                <Input
                  type="text"
                  name="city"
                  value={generalInfo.city}
                  onChange={handleGeneralInfoChange}
                  placeholder="Enter City"
                />
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}
            </div>

            {/* State Field */}
            <div>
              <Label>State</Label>
              {poc ? (
                <Input
                  type="text"
                  name="state"
                  value={generalInfo.state}
                  onChange={handleGeneralInfoChange}
                  placeholder="Enter State"
                />
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}
            </div>

            {/* Country Dropdown */}
            <div>
              <Label>Country</Label>
              {poc ? (
                <Select
                  name="country"
                  value={generalInfo.country}
                  onValueChange={(value) =>
                    handleGeneralInfoChange({
                      target: {
                        name: "country",
                        value: value,
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Malaysia">Malaysia</SelectItem>
                    <SelectItem value="Indonesia">Indonesia</SelectItem>
                    <SelectItem value="China">China</SelectItem>
                    <SelectItem value="Singapore">Singapore</SelectItem>
                    <SelectItem value="Vietnam">Vietnam</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}
            </div>

            {/* Google map link */}
            <div className="col-span-2">
              <Label>Map Location</Label>
              {poc ? (
                <Input
                  type="text"
                  name="map"
                  value={generalInfo.map}
                  onChange={handleGeneralInfoChange}
                  placeholder="Enter Google Map Link"
                />
              ) : (
                <Skeleton className="w-full h-10 rounded" />
              )}

              {/* Add the iframe here
              {generateIframe(gurl)} */}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete POC"
        description="Are you sure you want to delete this POC? This action is NOT reversible and will result in permanent loss."
        icon={<FaExclamationTriangle className="text-red-500" />}
        iconColor="red"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
            >
              No, Cancel
            </Button>
            <Button variant={"destructive"} onClick={handleConfirmDelete}>
              Yes, Delete this POC
            </Button>
          </div>
        }
      ></Modal>
    </div>
  );
};

export default EditPOCPage;
